import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { FlowMessage } from '../services/chat.flow.service';
import { UserService } from '../../../core/services/core.user.service';

@Component({
    selector: 'flow-message',
    template: require('./message.component.html'),
    styles: [`
        :host { display: block; }

        @keyframes flow-message-in {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0; }
        }
        .message-row {
            display: flex;
            margin-bottom: 20px;
            animation: flow-message-in 0.25s ease-out both;
        }
        .message-bubble {
            min-width: max(30%, 220px);
            max-width: 90%;
            border-radius: 5px;
            padding: 12px 16px;
        }
        .message-bubble.user {
            background-color: rgba(152, 223, 138, 0.05);
            border: 1px solid rgba(152, 223, 138, 0.6);
        }
        .message-bubble.assistant {
            background-color: #f5f5f5;
        }
        .message-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
            padding-bottom: 8px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }
        .message-sender-info {
            display: flex;
            align-items: center;
            gap: 7px;
        }
        .message-flow-glyph {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 22px;
            height: 22px;
            flex-shrink: 0;
        }
        .message-flow-glyph ::ng-deep svg {
            width: 22px;
            height: 22px;
        }
        .message-sender {
            font-weight: bold;
            font-size: 14px;
            color: #444;
        }
        .message-timestamp {
            font-size: 11px;
            color: #aaa;
        }
        .message-streaming-content {
            line-height: 1.6;
            color: #333;
        }
        .streaming-cursor {
            display: inline-block;
            width: 8px;
            height: 14px;
            background: #999;
            margin-left: 2px;
            vertical-align: text-bottom;
            animation: blink 1s step-end infinite;
        }
        .message-error {
            color: #a94442;
        }
        .tool-call-inline {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid rgba(0, 0, 0, 0.06);
            color: #888;
            font-size: 13px;
            font-style: italic;
        }
        .message-actions {
            display: flex;
            align-items: center;
            margin-top: 8px;
        }
    `]
})
export class MessageComponent implements OnChanges {

    @Input() message!: FlowMessage;
    @Input() isLast = false;
    @Input() activeToolLabel: string | null = null;
    @ViewChild('content', { static: false }) contentEl!: ElementRef;

    isCollapsible = false;
    isCollapsed = true;
    copied = false;

    constructor(private cdr: ChangeDetectorRef, private userService: UserService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isLast && !changes.isLast.firstChange && this.contentEl) {
            if (!this.isLast) {
                // Was the last message; now it's not — apply max-height and check if it overflows
                this.isCollapsed = true;
                this.cdr.detectChanges();
                const el = this.contentEl.nativeElement;
                if (el.scrollHeight > el.clientHeight) {
                    this.isCollapsible = true;
                } else {
                    this.isCollapsed = false;
                }
                this.cdr.detectChanges();
            } else {
                // Became last again (e.g. message after it was removed) — uncollapse
                this.isCollapsed = false;
                this.isCollapsible = false;
                this.cdr.detectChanges();
            }
            console.log("Message collapsible", this.isCollapsible, this.message);
        }
    }

    get isUser(): boolean {
        return this.message.role === 'user';
    }

    get isThinking(): boolean {
        return !!this.message.streaming && this.message.content === '';
    }

    get hasCitations(): boolean {
        return !!(this.message.citations && this.message.citations.length > 0);
    }

    showCitations = false;

    get currentUserUnixName(): string {
        return this.userService.getUnixName() || 'You';
    }

    get avatarUrl(): string {
        const unixName = this.userService.getUnixName() || '100';
        return `/api/user/${unixName}/avatar`;
    }

    onMarkdownReady(el: HTMLElement) {
        if (this.message.streaming) { return; }
        setTimeout(() => {
            if (el.scrollHeight > el.clientHeight) {
                this.isCollapsible = true;
            } else {
                this.isCollapsed = false;
            }
            this.cdr.detectChanges();
        }, 0);
    }

    toggleCollapse() {
        this.isCollapsed = !this.isCollapsed;
    }

    copyContent() {
        const text = this.message.content;
        const clipboard = this.getClipboardApi();
        if (clipboard) {
            clipboard.writeText(text)
                .then(() => this.setCopied())
                .catch(() => this.execCommandCopy(text));
        } else {
            this.execCommandCopy(text);
        }
    }

    private getClipboardApi(): { writeText(text: string): Promise<void> } | undefined {
        return (navigator as Navigator & {
            clipboard?: { writeText(text: string): Promise<void> };
        }).clipboard;
    }

    private setCopied() {
        this.copied = true;
        this.cdr.detectChanges();
        setTimeout(() => { this.copied = false; this.cdr.detectChanges(); }, 2000);
    }

    private execCommandCopy(text: string) {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
            document.execCommand('copy');
            this.setCopied();
        } catch (e) { /* ignore */ }
        document.body.removeChild(ta);
    }

    relativeDate(dateStr: string): string {
        const diffMs = Date.now() - new Date(dateStr).getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffMs < 1000 * 60 * 60 * 24) { return 'today'; }
        if (diffDays < 7) { return diffDays + 'd'; }
        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks < 5) { return diffWeeks + 'w'; }
        return Math.floor(diffDays / 30) + 'mo';
    }

    fullDate(dateStr: string): string {
        return new Date(dateStr).toLocaleString();
    }

}
